import React, { useState, useEffect, useMemo, useCallback } from "react";

import { Box, Stack, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import Button, { Color } from "../../../../../components/Form/Button";
import CodeDigitInput from "../../../../../components/Form/CodeDigitInput";
import { NotificationToast } from "../../../../../components/Generic/Toast/NotificationToast";
import { Error } from "../../../../../models/error";
import MfaService, { MfaDelivery, MfaMedium, MfaLoginResponse } from "../../../../../services/mfa.service";
import UserService from "../../../../../services/user.service";
import useStyles, {
  DangerContainer,
  ErrorContent,
  ErrorIcon,
  ErrorIconContainer,
  ErrorText,
  ErrorTextContainer,
  InfoContainer,
  InfoContent,
  InfoIconContainer,
  InfoIcon,
} from "./styles";

const CODE_LENGTH = 6;

const DANGER_ICON = require("../../../../../assets/icons/danger.svg");
const INFO_ICON = require("../../../../../assets/icons/info.svg");

export interface LoginPayload {
  email: string;
  password: string;
}

interface MfaVerificationProps {
  mfaSessionId: string;
  availableMethods: MfaMedium[];
  deliveries: MfaDelivery[];
  expiresInSeconds: number;
  onSuccess: (token: string, user: any, passwordResetToken?: string) => void;
  onBack?: () => void;
  purpose?: "LOGIN" | "PASSWORD_RESET";
  loginPayload?: LoginPayload;
}

function isSessionExpiredError(error: any): boolean {
  const backendError = error?.response?.data?.error ?? "";
  const backendMessage = (error?.response?.data?.message ?? "").toUpperCase();
  const normalized = `${String(backendError).toUpperCase()} ${backendMessage}`;
  return (
    normalized.includes("MFA") &&
    (normalized.includes("EXPIRED") || normalized.includes("SESSION") || normalized.includes("NOT_EXIST") || normalized.includes("NOT_FOUND"))
  );
}

const MfaVerification: React.FC<MfaVerificationProps> = ({
  mfaSessionId,
  availableMethods,
  deliveries,
  expiresInSeconds,
  onSuccess,
  onBack,
  purpose = "LOGIN",
  loginPayload,
}) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [code, setCode] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [timeRemaining, setTimeRemaining] = useState<number>(expiresInSeconds);
  const [activeMfaSessionId, setActiveMfaSessionId] = useState<string>(mfaSessionId);
  const [currentAvailableMethods, setCurrentAvailableMethods] = useState<MfaMedium[]>(availableMethods ?? []);
  const [currentDeliveries, setCurrentDeliveries] = useState<MfaDelivery[]>(deliveries ?? []);

  useEffect(() => {
    setActiveMfaSessionId(mfaSessionId);
  }, [mfaSessionId]);

  useEffect(() => {
    setCurrentAvailableMethods(availableMethods ?? []);
  }, [availableMethods]);

  useEffect(() => {
    setCurrentDeliveries(deliveries ?? []);
  }, [deliveries]);

  useEffect(() => {
    if (timeRemaining <= 0) return;

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [timeRemaining]);

  const refreshMfaSession = useCallback(async (): Promise<MfaLoginResponse | null> => {
    if (purpose !== "LOGIN" || !loginPayload?.email || !loginPayload?.password) {
      return null;
    }
    const response = await UserService.login(loginPayload.email, loginPayload.password);
    const data = response?.data;
    if (!data?.requiresMfa || !data?.mfaSessionId) {
      throw new Error("MFA_SESSION_REFRESH_FAILED");
    }
    setActiveMfaSessionId(data.mfaSessionId);
    setCurrentAvailableMethods(data.availableMethods ?? []);
    setCurrentDeliveries(data.deliveries ?? []);
    setTimeRemaining(data.expiresInSeconds ?? 300);
    setErrorMessage("");
    setCode("");
    return data as MfaLoginResponse;
  }, [purpose, loginPayload]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const isExpired = timeRemaining <= 0;

  const { mutate: validateCode, isLoading: isValidating } = useMutation(
    ["mfa.validate", activeMfaSessionId],
    () => MfaService.validateMfaCode(activeMfaSessionId, code),
    {
      onSuccess: (response) => {
        if (response?.data?.success) {
          setErrorMessage("");
          if (purpose === "PASSWORD_RESET") {
            onSuccess(
              response.data.token,
              response.data.user,
              response.data.passwordResetToken
            );
          } else {
            onSuccess(response.data.token, response.data.user);
          }
        }
      },
      onError: (err: AxiosError<Error>) => {
        const errorMsg = err.response?.data?.message ?? translate("invalidCode") ?? "";
        setErrorMessage(errorMsg);
      },
    }
  );

  const { mutate: resendCode, isLoading: isResending } = useMutation(
    ["mfa.resend"],
    ({ medium, sessionId }: { medium?: MfaMedium; sessionId: string }) =>
      MfaService.resendMfaCode({
        mfaSessionId: sessionId,
        medium,
      }),
    {
      onSuccess: (response) => {
        if (response?.data?.success) {
          setErrorMessage("");
          setTimeRemaining(response.data.expiresInSeconds);
          setCurrentAvailableMethods(response.data.availableMethods ?? []);
          setCurrentDeliveries(response.data.deliveries ?? []);
          NotificationToast.success(translate("mfaCodeResent"));
        }
      },
      onError: (err: AxiosError<Error>) => {
        if (isSessionExpiredError(err)) {
          NotificationToast.error(translate("codeExpired") ?? "");
          return;
        }
        const errorMsg = err.response?.data?.message ?? translate("errorResendingCode") ?? "";
        NotificationToast.error(errorMsg);
      },
    }
  );

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (code.length === 6) {
      validateCode();
    }
  };

  const handleResend = async (medium?: MfaMedium) => {
    let sessionIdToUse = activeMfaSessionId;

    if (isExpired && loginPayload?.email && loginPayload?.password) {
      try {
        const refreshed = await refreshMfaSession();
        if (!refreshed?.mfaSessionId) {
          NotificationToast.error(translate("codeExpired") ?? "");
          return;
        }
        sessionIdToUse = refreshed.mfaSessionId;
      } catch {
        NotificationToast.error(translate("codeExpired") ?? "");
        return;
      }
    }

    resendCode({ medium, sessionId: sessionIdToUse });
  };

  const canResend = !isResending && !isValidating;
  const canResendWhenExpired = isExpired && !!loginPayload?.email && !!loginPayload?.password;

  const deliveryText = useMemo(() => {
    if (currentDeliveries.length === 0) return "";
    if (currentDeliveries.length === 1) {
      const delivery = currentDeliveries[0];
      return `${delivery.medium === MfaMedium.EMAIL ? translate("email") : translate("sms")}: ${
        delivery.destination
      }`;
    }
    return currentDeliveries
      .map(
        (d) =>
          `${d.medium === MfaMedium.EMAIL ? translate("email") : translate("sms")}: ${d.destination}`
      )
      .join(" • ");
  }, [currentDeliveries, translate]);

  return (
    <Box className={classes.boxContainer}>
      <Stack
        className={classes.mainGrid}
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={3}
      >
        <h2 className={classes.title}>{translate("mfaVerificationTitle")}</h2>

        <InfoContainer>
          <InfoContent>
            <InfoIconContainer>
              <InfoIcon src={INFO_ICON} alt="Info icon" />
            </InfoIconContainer>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {translate("mfaVerificationDescription")}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {deliveryText}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" sx={{ color: isExpired ? "error.main" : "text.secondary" }}>
                  {isExpired ? translate("codeExpired") : `${translate("expiresIn")}: ${formatTime(timeRemaining)}`}
                </Typography>
              </Stack>
            </Box>
          </InfoContent>
        </InfoContainer>

        {errorMessage && (
          <DangerContainer>
            <ErrorContent>
              <ErrorIconContainer>
                <ErrorIcon src={DANGER_ICON} alt="Error icon" />
              </ErrorIconContainer>
              <ErrorTextContainer>
                <ErrorText variant="body2">{errorMessage}</ErrorText>
              </ErrorTextContainer>
            </ErrorContent>
          </DangerContainer>
        )}

        <form className="w-100" onSubmit={handleSubmit}>
          <Stack width="100%" justifyContent="center" alignItems="stretch" spacing={3}>
            <CodeDigitInput
              value={code}
              onChange={(newCode) => {
                setCode(newCode);
                setErrorMessage("");
              }}
              length={CODE_LENGTH}
              label={translate("mfaVerificationCode")}
              required
              disabled={isExpired}
              error={!!errorMessage}
              errorMessage={errorMessage}
              data-cy="mfaCodeDigit"
            />

            <Stack width="100%" direction="column" spacing={2} alignItems="stretch">
              <Button
                type="submit"
                fullWidth
                customColor={Color.BRAND}
                disabled={code.length !== 6 || isValidating || isExpired}
                loading={isValidating}
                data-cy="mfaVerifyButton"
                aria-label="mfaVerifyButton"
              >
                {translate("verify")}
              </Button>

              {currentAvailableMethods.length === 1 && (
                <Button
                  type="button"
                  fullWidth
                  customColor={Color.SECONDARY}
                  disabled={!canResend && !canResendWhenExpired}
                  loading={isResending}
                  onClick={() => handleResend()}
                  data-cy="mfaResendButton"
                  aria-label="mfaResendButton"
                >
                  {translate("resendCode")}
                </Button>
              )}

              {currentAvailableMethods.length > 1 && (
                <Stack direction="row" spacing={1} width="100%">
                  {currentAvailableMethods.map((method) => (
                    <Button
                      key={method}
                      type="button"
                      fullWidth
                      customColor={Color.SECONDARY}
                      disabled={!canResend && !canResendWhenExpired}
                      loading={isResending}
                      onClick={() => handleResend(method)}
                      data-cy={`mfaResend${method}Button`}
                      aria-label={`mfaResend${method}Button`}
                    >
                      {method === MfaMedium.EMAIL
                        ? translate("resendViaEmail")
                        : translate("resendViaSms")}
                    </Button>
                  ))}
                </Stack>
              )}

              {onBack && (
                <Button
                  type="button"
                  fullWidth
                  customColor={Color.SECONDARY}
                  onClick={onBack}
                  data-cy="mfaBackButton"
                  aria-label="mfaBackButton"
                >
                  {translate("back")}
                </Button>
              )}
            </Stack>
          </Stack>
        </form>
      </Stack>
    </Box>
  );
};

export default MfaVerification;
